import { useRef, useState } from "react";

import { UIIconProps } from "common/blocks/UIIcon";

export type Toast = {
    title?: string;
    titleIcon?: UIIconProps;
    content: {
        component: any;
        props?: { [key: string]: any };
    };
    duration?: number; // in seconds.
};

export type RegisteredToasts = {
    [id: string]: Toast;
};

export interface IUseToast {
    toast: boolean;
    openToast: (toast: Toast, params?: { [key: string]: any }) => void;
    closeToast: () => void;
    toastContent: Toast | undefined;
    registerToasts: (popups: RegisteredToasts) => void;
    openRegisteredToast: (
        id: string,
        params?: { [key: string]: any },
        globalParams?: { [key: string]: any }
    ) => void;
}

const useToast = (): IUseToast => {
    const [toast, setToast] = useState<boolean>(false);
    const [toastContent, setToastContent] = useState<Toast | undefined>();
    const [registeredToasts, setRegisteredToasts] = useState<RegisteredToasts>({});

    const closeTimeout = useRef<NodeJS.Timer | null>(null);

    const setCloseTimeout = (duration: number = 10) => {
        closeTimeout.current = setTimeout(() => {
            closeToast();
        }, duration * 1000);
    };

    const resetTimeout = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    };

    const closeToast = () => {
        setToast(false);
        setToastContent(undefined);

        if (closeTimeout.current) {
            resetTimeout();
        }
    };

    const openToast = (toast: Toast, params: { [key: string]: any } = {}) => {
        resetTimeout();
        setToastContent({
            ...toast,
            content: {
                ...toast.content,
                props: {
                    ...toast.content.props,
                    params,
                },
            },
        });
        setToast(true);
        setCloseTimeout(toast.duration);
    };

    const openRegisteredToast = (
        id: string,
        params: { [key: string]: any } = {},
        globalParams: { [key: string]: any } = {}
    ) => {
        if (!registeredToasts[id]) throw new Error(`No registered toasts with id: ${id}`);
        resetTimeout();
        setToastContent({
            ...registeredToasts[id],
            content: {
                ...registeredToasts[id].content,
                props: {
                    ...registeredToasts[id].content.props,
                    params,
                },
            },
            ...globalParams,
        });
        setToast(true);
        setCloseTimeout(registeredToasts[id].duration);
    };

    const registerToasts = (toasts: RegisteredToasts) => {
        setRegisteredToasts((alreadyRegistered) => Object.assign(alreadyRegistered, toasts));
    };

    return {
        toast,
        toastContent,
        openToast,
        registerToasts,
        openRegisteredToast,
        closeToast,
    };
};

export default useToast;
